// GrantWinnersAdminTab.jsx — Admin Panel "Grant awards" sub-tab, extracted from
// LimitedPartnerPortal. Editable winner cards (about, website, photo, award
// amount, published toggle) with per-pitch pending-change save, plus the "Log
// grant award" form for grants issued outside the app process (live pitch
// competitions, school events). Logged awards become `pitches` docs with
// source: "manual" — see helpers/manualAward.js for how they thread through
// totals while staying out of review flows. The pendingChanges / photosById /
// aboutById / websiteById maps stay lifted in the portal because
// handleAssignWinner (Reviews sub-tab) and the save flow both touch them.
import React, { useState } from "react";
import { addDoc, collection, doc, updateDoc, Timestamp } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { httpsCallable } from "firebase/functions";
import { db, storage, functions } from "../../../../firebaseConfig.js";
import { RetroButton, EmptyState } from "../../../ui/retro";
import {
  resolveAwardAmount,
  sumAwarded,
  chaptersByNameMap,
} from "../../../../helpers/awardAmount";
import {
  MANUAL_AWARD_SOURCE,
  isManualAward,
  manualAwardQuarterOptions,
  quarterToDate,
} from "../../../../helpers/manualAward";
import { SELF_ID_CATEGORIES } from "../../statistics/statsAggregations";

const generateAboutCallable = httpsCallable(functions, "generateAboutFromApplication");

const emptyAwardDraft = {
  businessName: "",
  founderName: "",
  chapter: "",
  quarter: "",
  awardAmount: "",
  website: "",
  zipCode: "",
  about: "",
  selfIdentification: [],
};

export default function GrantWinnersAdminTab({
  user,
  isChapterDirector,
  activeChapterNames,
  adminPitches,
  chapters,
  pendingChanges,
  setPendingChanges,
  aboutById,
  setAboutById,
  websiteById,
  setWebsiteById,
  photosById,
  setPhotosById,
  generatingAboutId,
  setGeneratingAboutId,
  uploadingPhotoFor,
  setUploadingPhotoFor,
  winnerChapterFilter,
  setWinnerChapterFilter,
  winnerSearchTerm,
  setWinnerSearchTerm,
  loadAdminData,
  showAppAlert,
}) {
            // Source from the raw admin list, not adminFilteredSortedPitches.
            // The Pitches & Reviews tab's filters/sort must not leak in here —
            // Grant awards has its own chapter + search controls below.
            const winnerPitches = adminPitches.filter(p => p.isWinner);
            // Chapter-name lookup used by the award-amount fallback chain and
            // the log-award form's default amount.
            const chaptersLookup = chaptersByNameMap(chapters);

            // ── Log grant award (ad hoc, outside the app process) ──
            // Draft state is local: nothing outside this tab touches it, and a
            // discarded draft on tab switch is the right behavior.
            const [showLogForm, setShowLogForm] = useState(false);
            const [awardDraft, setAwardDraft] = useState(emptyAwardDraft);
            const [isLoggingAward, setIsLoggingAward] = useState(false);

            const quarterChoices = manualAwardQuarterOptions();
            // Directors log into their own chapter only (rules enforce the same);
            // superAdmins pick from the active chapter list.
            const draftChapter = isChapterDirector ? (user?.chapter || "") : awardDraft.chapter;
            const draftChapterDefault = (() => {
              const chapterDoc = chaptersLookup.get(draftChapter);
              const d = Number(chapterDoc?.defaultGrantAmount);
              return Number.isFinite(d) && d > 0 ? d : 1000;
            })();

            const toggleDraftSelfId = (tag) => {
              setAwardDraft(prev => ({
                ...prev,
                selfIdentification: prev.selfIdentification.includes(tag)
                  ? prev.selfIdentification.filter(t => t !== tag)
                  : [...prev.selfIdentification, tag],
              }));
            };

            const handleLogAward = async (e) => {
              e.preventDefault();
              const businessName = awardDraft.businessName.trim();
              const founderName = awardDraft.founderName.trim();
              const quarter = awardDraft.quarter || quarterChoices[0];
              if (!businessName || !founderName || !draftChapter) {
                showAppAlert("Business name, founder name, and chapter are required.");
                return;
              }
              const awardDate = quarterToDate(quarter);
              if (!awardDate) {
                showAppAlert("Pick the quarter the grant was issued.");
                return;
              }
              const rawAmount = awardDraft.awardAmount.trim();
              const parsedAmount = rawAmount === ""
                ? draftChapterDefault
                : Number(rawAmount.replace(/[$,]/g, ""));
              if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
                showAppAlert("Award amount must be a positive number.");
                return;
              }

              setIsLoggingAward(true);
              try {
                const awardTimestamp = Timestamp.fromDate(awardDate);
                // Same doc shape handleAssignWinner produces, plus the manual
                // marker. createdAt is back-dated into the issued quarter —
                // quarter labels everywhere derive from it. New awards start
                // hidden; the Live on website toggle publishes them (which is
                // when syncPublicWinner mirrors them into public totals).
                await addDoc(collection(db, "pitches"), {
                  source: MANUAL_AWARD_SOURCE,
                  businessName,
                  founderName,
                  chapter: draftChapter,
                  zipCode: awardDraft.zipCode.trim(),
                  website: awardDraft.website.trim(),
                  about: awardDraft.about.trim(),
                  selfIdentification: awardDraft.selfIdentification,
                  isWinner: true,
                  awardAmount: Math.round(parsedAmount),
                  winnerPublished: false,
                  createdAt: awardTimestamp,
                  awardedAt: awardTimestamp,
                  createdByUid: user?.uid || "",
                });
                showAppAlert(`Grant award logged: "${businessName}" at $${Math.round(parsedAmount).toLocaleString()} for ${quarter}. It starts hidden: add a photo below, then flip "Live on website" to publish.`);
                setAwardDraft(emptyAwardDraft);
                setShowLogForm(false);
                loadAdminData();
              } catch (err) {
                console.error("Log grant award failed:", err);
                showAppAlert(`Failed to log award: ${err.message || err}`);
              } finally {
                setIsLoggingAward(false);
              }
            };

            const handleFieldChange = (pitchId, field, value) => {
              if (field === "about") {
                setAboutById(prev => ({ ...prev, [pitchId]: value }));
              } else if (field === "website") {
                setWebsiteById(prev => ({ ...prev, [pitchId]: value }));
              } else if (field === "pitch-photo") {
                setPhotosById(prev => ({ ...prev, [pitchId]: value }));
              }
            
              setPendingChanges(prev => ({
                ...prev,
                [pitchId]: {
                  ...(prev[pitchId] || {}),
                  [field]: value
                }
              }));
            };

            const handleGenerateAbout = async (pitchId) => {
              const existing = aboutById[pitchId];
              const fallback = (winnerPitches.find(p => p.id === pitchId) || {}).about || "";
              const current = (existing ?? fallback).trim();
              if (current && !window.confirm("Replace the current About text with an AI-generated version?")) return;
              setGeneratingAboutId(pitchId);
              try {
                const result = await generateAboutCallable({ pitchId });
                const text = result?.data?.about || "";
                if (!text) {
                  showAppAlert("AI returned no text.");
                } else {
                  handleFieldChange(pitchId, "about", text);
                  showAppAlert("Generated. Review and Save All Changes when ready.");
                }
              } catch (err) {
                console.error("Generate about failed:", err);
                showAppAlert(`Generate failed: ${err.message || err}`);
              }
              setGeneratingAboutId(null);
            };

            // Upload a grant winner's photo to pitch-photos/ in Firebase Storage,
            // then write the URL into the pitch's `pitch-photo` field. Storage
            // permissions are enforced by storage.rules (any portal user, image
            // ≤ 5 MB). Saves immediately to Firestore so it survives a tab close —
            // independent of the Save All Changes button that batches About /
            // Website edits.
            const handlePhotoUpload = async (pitchId, file) => {
              if (!file) return;
              if (!file.type.startsWith('image/')) {
                showAppAlert('Please choose an image file (PNG, JPG, etc.).');
                return;
              }
              if (file.size > 5 * 1024 * 1024) {
                showAppAlert('Image must be smaller than 5 MB.');
                return;
              }

              const ext = (file.name.split('.').pop() || 'jpg').toLowerCase().replace(/[^a-z0-9]/g, '') || 'jpg';
              const path = `pitch-photos/${pitchId}_${Date.now()}.${ext}`;

              setUploadingPhotoFor(prev => ({ ...prev, [pitchId]: true }));
              try {
                const storageRef = ref(storage, path);
                await uploadBytes(storageRef, file, { contentType: file.type });
                const photoURL = await getDownloadURL(storageRef);

                // Persist immediately. Field name matches what FounderMap and the
                // public-facing pages read (`pitch-photo`, hyphenated — historical).
                await updateDoc(doc(db, "pitches", pitchId), { "pitch-photo": photoURL });

                setPhotosById(prev => ({ ...prev, [pitchId]: photoURL }));
                // Drop any stale pending edit for this field so the Save button
                // state stays accurate after the immediate write.
                setPendingChanges(prev => {
                  const next = { ...prev };
                  if (next[pitchId]) {
                    const { ["pitch-photo"]: _drop, ...rest } = next[pitchId];
                    if (Object.keys(rest).length === 0) delete next[pitchId];
                    else next[pitchId] = rest;
                  }
                  return next;
                });

                showAppAlert('Photo uploaded and saved.');
                loadAdminData();
              } catch (error) {
                console.error('Pitch photo upload failed:', error);
                let errorMessage = 'Failed to upload photo: ';
                if (error.code === 'storage/unauthorized') {
                  errorMessage += 'You do not have permission to upload files.';
                } else if (error.code === 'storage/canceled') {
                  errorMessage += 'Upload was canceled.';
                } else {
                  errorMessage += error.message || 'Unknown error.';
                }
                showAppAlert(errorMessage);
              } finally {
                setUploadingPhotoFor(prev => {
                  const next = { ...prev };
                  delete next[pitchId];
                  return next;
                });
              }
            };

            // Toggle whether a winner is live on the public site (awardees grid,
            // founder map, mobile landing, public pitch detail). Missing field
            // reads as published — only docs explicitly set to false are hidden.
            const handleTogglePublished = async (pitchId, nextPublished) => {
              try {
                await updateDoc(doc(db, "pitches", pitchId), {
                  winnerPublished: nextPublished
                });
                loadAdminData();
              } catch (err) {
                console.error('Toggle publish error:', err);
                showAppAlert(`Failed to update visibility: ${err.message}`);
              }
            };

            const handleSaveAllChanges = async (pitchId) => {
              const changes = pendingChanges[pitchId] || {};
              if (Object.keys(changes).length === 0) {
                showAppAlert('No changes to save.');
                return;
              }

              try {
                // Update all changes at once
                await updateDoc(doc(db, "pitches", pitchId), changes);
                
                // Clear pending changes for this pitch
                setPendingChanges(prev => {
                  const newPending = { ...prev };
                  delete newPending[pitchId];
                  return newPending;
                });
                
                showAppAlert('All changes saved successfully!');
                loadAdminData();
              } catch (err) {
                console.error('Save error:', err);
                showAppAlert(`Failed to save changes: ${err.message}`);
              }
            };

            const filteredWinners = winnerPitches
              .filter(p => !winnerChapterFilter || p.chapter === winnerChapterFilter)
              .filter(p => !winnerSearchTerm || 
                p.businessName?.toLowerCase().includes(winnerSearchTerm.toLowerCase()) ||
                p.founderName?.toLowerCase().includes(winnerSearchTerm.toLowerCase())
              );

            const winnerCount = winnerPitches.length;
            const shownCount = filteredWinners.length;
            // Sum actual awardAmount per winner, falling back to chapter default
            // then to the historical $1,000. Replaces the prior hardcoded constant
            // that ignored variable grants.
            const totalAwarded = sumAwarded(winnerPitches, chaptersLookup);
            // Awarded now matches the public impact totals: hidden winners
            // mirror into publicWinners as redacted stubs, so their dollars
            // count publicly before the announcement. Live on site is the
            // published-only sum — the winners actually visible in the public
            // grid and map.
            const publishedWinners = winnerPitches.filter(p => p.winnerPublished !== false);
            const liveAwarded = sumAwarded(publishedWinners, chaptersLookup);
            const hiddenCount = winnerCount - publishedWinners.length;
            return (
              <>
                <div className="win95-pane-header">
                  <div>
                    <div className="win95-pane-header__context">
                      {winnerChapterFilter || "All chapters"} · since 2023
                    </div>
                    <h2 className="win95-pane-header__title">Grant awards</h2>
                  </div>
                  <div className="win95-pane-header__stats">
                    <div className="win95-statpanel">
                      <div className="win95-statpanel__label">Winners</div>
                      <div className="win95-statpanel__figure" style={{ color: "var(--mb-magenta-deep)" }}>
                        {winnerCount}
                      </div>
                    </div>
                    <div className="win95-statpanel" title="Every logged award, hidden ones included. Public impact totals count this same figure.">
                      <div className="win95-statpanel__label">Awarded</div>
                      <div className="win95-statpanel__figure" style={{ color: "var(--mb-tangerine-deep)" }}>
                        ${totalAwarded.toLocaleString()}
                      </div>
                    </div>
                    <div
                      className="win95-statpanel"
                      title={`Winners visible on the public site (grid and map).${hiddenCount > 0 ? ` ${hiddenCount} hidden winner${hiddenCount === 1 ? '' : 's'} still count in public dollar totals, without identity.` : ''}`}
                    >
                      <div className="win95-statpanel__label">Live on site</div>
                      <div className="win95-statpanel__figure" style={{ color: "var(--mb-tangerine-deep)" }}>
                        ${liveAwarded.toLocaleString()}
                      </div>
                    </div>
                    <div className="win95-statpanel">
                      <div className="win95-statpanel__label">Shown</div>
                      <div className="win95-statpanel__figure" style={{ color: "var(--mb-aqua-deep)" }}>
                        {shownCount}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="admin-pane-body">
                {/* Filters */}
                <div className="filter-bar">
                  <select
                    aria-label="Filter by chapter"
                    value={winnerChapterFilter}
                    onChange={(e) => setWinnerChapterFilter(e.target.value)}
                  >
                    <option value="">All chapters</option>
                    {[...new Set(winnerPitches.map(p => p.chapter).filter(Boolean))].sort().map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                  <input
                    type="search"
                    aria-label="Search winners"
                    placeholder="Search business or founder…"
                    value={winnerSearchTerm}
                    onChange={(e) => setWinnerSearchTerm(e.target.value)}
                  />
                  <RetroButton
                    variant={showLogForm ? 'default' : 'primary'}
                    onClick={() => setShowLogForm(v => !v)}
                    style={{ textTransform: 'none', flexShrink: 0 }}
                  >
                    {showLogForm ? 'Close log form' : 'Log grant award'}
                  </RetroButton>
                </div>

                {/* Ad hoc award entry: grants issued outside the app process
                    (live pitch competitions, school events, partner programs).
                    Writes a source:"manual" pitch doc — counted in every
                    winner/dollar total, invisible to review flows. */}
                {showLogForm && (
                  <form onSubmit={handleLogAward}>
                    <fieldset className="win95-groupbox" style={{ marginBottom: 14 }}>
                      <legend>Log a grant award</legend>
                      <p style={{ marginTop: 0, marginBottom: 10, fontSize: '0.85em', color: 'var(--mb-ink-60)' }}>
                        For grants issued outside the app process: live pitch competitions, school
                        competitions, partner events. Logged awards count toward chapter and GNF
                        totals immediately. They start hidden and appear on the public site when
                        you flip "Live on website".
                      </p>

                      <div className="retro-form-grid">
                        <div className="admin-field-row" style={{ marginBottom: 0 }}>
                          <label htmlFor="log-award-business">
                            Business name <span aria-hidden="true" style={{ color: 'var(--mb-magenta)' }}>*</span>
                          </label>
                          <input
                            id="log-award-business"
                            type="text"
                            value={awardDraft.businessName}
                            onChange={(e) => setAwardDraft(prev => ({ ...prev, businessName: e.target.value }))}
                            required
                          />
                        </div>
                        <div className="admin-field-row" style={{ marginBottom: 0 }}>
                          <label htmlFor="log-award-founder">
                            Founder name <span aria-hidden="true" style={{ color: 'var(--mb-magenta)' }}>*</span>
                          </label>
                          <input
                            id="log-award-founder"
                            type="text"
                            value={awardDraft.founderName}
                            onChange={(e) => setAwardDraft(prev => ({ ...prev, founderName: e.target.value }))}
                            required
                          />
                        </div>
                        <div className="admin-field-row" style={{ marginBottom: 0 }}>
                          <label htmlFor="log-award-chapter">
                            Chapter <span aria-hidden="true" style={{ color: 'var(--mb-magenta)' }}>*</span>
                          </label>
                          <select
                            id="log-award-chapter"
                            value={draftChapter}
                            onChange={(e) => setAwardDraft(prev => ({ ...prev, chapter: e.target.value }))}
                            disabled={isChapterDirector}
                            title={isChapterDirector ? `Chapter directors log awards for ${user?.chapter}.` : undefined}
                            required
                          >
                            {isChapterDirector ? (
                              <option value={user?.chapter || ""}>{user?.chapter}</option>
                            ) : (
                              <>
                                <option value="">Select chapter</option>
                                {(activeChapterNames || []).map(name => (
                                  <option key={name} value={name}>{name}</option>
                                ))}
                              </>
                            )}
                          </select>
                        </div>
                        <div className="admin-field-row" style={{ marginBottom: 0 }}>
                          <label htmlFor="log-award-quarter">
                            Quarter issued <span aria-hidden="true" style={{ color: 'var(--mb-magenta)' }}>*</span>
                          </label>
                          <select
                            id="log-award-quarter"
                            value={awardDraft.quarter || quarterChoices[0]}
                            onChange={(e) => setAwardDraft(prev => ({ ...prev, quarter: e.target.value }))}
                            required
                          >
                            {quarterChoices.map(q => (
                              <option key={q} value={q}>{q}</option>
                            ))}
                          </select>
                        </div>
                        <div className="admin-field-row" style={{ marginBottom: 0 }}>
                          <label htmlFor="log-award-amount">Award amount ($)</label>
                          <input
                            id="log-award-amount"
                            type="number"
                            min="1"
                            step="1"
                            inputMode="numeric"
                            value={awardDraft.awardAmount}
                            onChange={(e) => setAwardDraft(prev => ({ ...prev, awardAmount: e.target.value }))}
                            placeholder={`${draftChapterDefault.toLocaleString()} (chapter default)`}
                          />
                        </div>
                        <div className="admin-field-row" style={{ marginBottom: 0 }}>
                          <label htmlFor="log-award-zip">Zip code</label>
                          <input
                            id="log-award-zip"
                            type="text"
                            inputMode="numeric"
                            value={awardDraft.zipCode}
                            onChange={(e) => setAwardDraft(prev => ({ ...prev, zipCode: e.target.value }))}
                            placeholder="Optional"
                          />
                        </div>
                        <div className="admin-field-row" style={{ marginBottom: 0 }}>
                          <label htmlFor="log-award-website">Website</label>
                          <input
                            id="log-award-website"
                            type="text"
                            value={awardDraft.website}
                            onChange={(e) => setAwardDraft(prev => ({ ...prev, website: e.target.value }))}
                            placeholder="https://..."
                          />
                        </div>
                      </div>

                      <div style={{ marginTop: 10 }}>
                        <label htmlFor="log-award-about" style={{ display: 'block', marginBottom: 4 }}>
                          <strong>About section</strong>
                        </label>
                        <textarea
                          id="log-award-about"
                          rows={3}
                          value={awardDraft.about}
                          onChange={(e) => setAwardDraft(prev => ({ ...prev, about: e.target.value }))}
                          placeholder="Short public description of the business or founder. You can also add this later from the card below."
                          style={{ display: 'block', width: '100%', padding: '6px 10px', fontFamily: 'inherit', fontSize: '0.95em', border: '2px solid', borderColor: 'var(--bevel-sunken)', boxShadow: 'none', background: 'var(--gnf-bg)', boxSizing: 'border-box', resize: 'vertical' }}
                        />
                      </div>

                      {/* Optional celebration tags — these feed the public
                          impact stats (share of winners who are women or
                          BIPOC owned/led), same as application winners. */}
                      <div style={{ marginTop: 10 }}>
                        <div style={{ marginBottom: 4 }}><strong>Self-identification</strong> <span style={{ fontSize: '0.8em', color: 'var(--mb-ink-60)' }}>(optional, feeds impact stats)</span></div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 14px' }}>
                          {SELF_ID_CATEGORIES.map(tag => (
                            <label key={tag} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: '0.85em', cursor: 'pointer', userSelect: 'none' }}>
                              <input
                                type="checkbox"
                                checked={awardDraft.selfIdentification.includes(tag)}
                                onChange={() => toggleDraftSelfId(tag)}
                              />
                              {tag}
                            </label>
                          ))}
                        </div>
                      </div>

                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
                        <RetroButton
                          type="button"
                          onClick={() => { setAwardDraft(emptyAwardDraft); setShowLogForm(false); }}
                          style={{ textTransform: 'none' }}
                          disabled={isLoggingAward}
                        >
                          Cancel
                        </RetroButton>
                        <RetroButton
                          type="submit"
                          variant="primary"
                          style={{ textTransform: 'none' }}
                          disabled={isLoggingAward}
                        >
                          {isLoggingAward ? 'Logging…' : 'Log award'}
                        </RetroButton>
                      </div>
                    </fieldset>
                  </form>
                )}

                {filteredWinners.length === 0 && (
                  <EmptyState
                    icon={null}
                    title="No grant awards match"
                    description="Try clearing a filter. Awards appear here after an admin marks a pitch as winning from the Reviews tab, or logs one with the Log grant award button above."
                  />
                )}

                {filteredWinners.map((p) => {
                  const pendingAmount = pendingChanges[p.id]?.awardAmount;
                  const currentAmount = pendingAmount !== undefined
                    ? pendingAmount
                    : (Number.isFinite(Number(p.awardAmount)) && Number(p.awardAmount) > 0 ? Number(p.awardAmount) : '');
                  const resolvedDisplay = resolveAwardAmount(p, chaptersLookup);
                  // Missing field = published (preserves pre-toggle behavior for
                  // existing winners). Only explicit false hides from public site.
                  const isPublished = p.winnerPublished !== false;
                  // Winner card — genuinely card-shaped (photo + editable
                  // about/website/amount), so it stays a card: raised chrome.
                  return (
                  <div key={p.id} style={{ marginBottom: '10px', padding: '10px 12px', border: '2px solid', borderColor: 'var(--bevel-raised)', boxShadow: 'var(--shadow-raised)', background: 'var(--chrome-face)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                      <h5 style={{ marginTop: 0, marginBottom: 0 }}>
                        {p.businessName} <span style={{ color: 'var(--mb-ink-60)', fontWeight: 'normal' }}>by {p.founderName}</span>
                        {!isPublished && (
                          <span style={{
                            marginLeft: 8,
                            padding: '2px 8px',
                            fontSize: '0.7em',
                            fontFamily: 'var(--font-chrome)',
                            fontWeight: 700,
                            letterSpacing: 'var(--track-label)',
                            textTransform: 'none',
                            background: 'var(--gnf-bg-dark)',
                            color: 'var(--mb-chalk)',
                            border: '1px solid var(--chrome-outline)'
                          }}>Hidden</span>
                        )}
                        {isManualAward(p) && (
                          <span style={{
                            marginLeft: 8,
                            padding: '2px 8px',
                            fontSize: '0.7em',
                            fontFamily: 'var(--font-chrome)',
                            fontWeight: 700,
                            letterSpacing: 'var(--track-label)',
                            textTransform: 'none',
                            background: 'var(--mb-aqua-deep)',
                            color: 'var(--mb-chalk)',
                            border: '1px solid var(--chrome-outline)'
                          }}>Logged manually</span>
                        )}
                        <span style={{ marginLeft: 10, fontFamily: 'var(--font-numeral)', fontSize: 11, fontWeight: 400, color: 'var(--chrome-text-soft)' }}>
                          {p.chapter} · {p.quarter}{p.zipCode ? ` · ${p.zipCode}` : ''}
                        </span>
                      </h5>
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: '0.85em', userSelect: 'none' }}>
                        <span className="win95-toggle">
                          <input
                            type="checkbox"
                            role="switch"
                            checked={isPublished}
                            onChange={(e) => handleTogglePublished(p.id, e.target.checked)}
                          />
                          <span className="win95-toggle__track" />
                          <span className="win95-toggle__thumb" />
                        </span>
                        <strong>Live on website</strong>
                      </label>
                    </div>

                    {/* Two columns: photo + upload on the left, fields on the right */}
                    <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap', marginTop: 10 }}>
                      {/* Pitch photo: direct upload to pitch-photos/ in Firebase Storage. */}
                      {(() => {
                        const currentPhoto = photosById[p.id] || p["pitch-photo"] || null;
                        const isUploading = !!uploadingPhotoFor[p.id];
                        return (
                          <div style={{ flex: 'none', width: 128, display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {currentPhoto ? (
                              <img
                                src={currentPhoto}
                                alt={`${p.businessName || 'Pitch'} photo`}
                                style={{
                                  width: 128, height: 128, objectFit: 'cover',
                                  border: '2px solid', borderColor: 'var(--bevel-sunken)',
                                  background: 'var(--mb-chalk)', display: 'block', boxSizing: 'border-box'
                                }}
                                onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
                              />
                            ) : (
                              <div style={{
                                width: 128, height: 128, boxSizing: 'border-box',
                                border: '2px dashed var(--mb-ink-40)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: 11, color: 'var(--mb-ink-60)', textAlign: 'center', padding: 4
                              }}>
                                No photo
                              </div>
                            )}
                            <label
                              htmlFor={`pitchPhoto-${p.id}`}
                              className="win95-btn win95-btn--compact"
                              style={{ justifyContent: 'center', cursor: isUploading ? 'default' : 'pointer', opacity: isUploading ? 0.7 : 1 }}
                            >
                              {isUploading ? 'Uploading…' : 'Upload photo'}
                            </label>
                            <input
                              id={`pitchPhoto-${p.id}`}
                              type="file"
                              accept="image/*"
                              disabled={isUploading}
                              onChange={(e) => {
                                const file = e.target.files && e.target.files[0];
                                if (file) handlePhotoUpload(p.id, file);
                                e.target.value = '';
                              }}
                              style={{ display: 'none' }}
                            />
                            <div style={{ fontSize: 10, lineHeight: 1.35, color: 'var(--mb-ink-60)' }}>
                              PNG or JPG, max 5 MB. Saves immediately.
                            </div>
                          </div>
                        );
                      })()}

                      <div style={{ flex: 1, minWidth: 280, display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {/* Award amount — feeds Statistics tab dollar totals. Blank defers
                            to the chapter default; resolvedDisplay shows the effective value. */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <label htmlFor={`awardAmount-${p.id}`}>
                            <strong>Award amount:</strong>
                          </label>
                          <span style={{ fontSize: '0.9em' }}>$</span>
                          <input
                            id={`awardAmount-${p.id}`}
                            type="number"
                            min="0"
                            step="1"
                            inputMode="numeric"
                            value={currentAmount}
                            placeholder={`${resolvedDisplay}`}
                            onChange={(e) => {
                              const raw = e.target.value;
                              setPendingChanges(prev => {
                                const next = { ...prev };
                                const pitchChanges = { ...(next[p.id] || {}) };
                                if (raw === '') {
                                  // Blank clears any pending amount edit. Persisted value stays what it is;
                                  // to reset to chapter default, admins re-mark the winner (deleteField path).
                                  delete pitchChanges.awardAmount;
                                } else {
                                  const parsed = Number(raw);
                                  if (Number.isFinite(parsed) && parsed > 0) {
                                    pitchChanges.awardAmount = Math.round(parsed);
                                  }
                                }
                                if (Object.keys(pitchChanges).length === 0) delete next[p.id];
                                else next[p.id] = pitchChanges;
                                return next;
                              });
                            }}
                            style={{ width: 100, padding: '3px 8px', fontFamily: 'var(--font-numeral)', border: '2px solid', borderColor: 'var(--bevel-sunken)', boxShadow: 'none', background: 'var(--gnf-bg)' }}
                          />
                          <span style={{ fontSize: '0.8em', color: 'var(--mb-ink-60)' }}>
                            Effective: ${resolvedDisplay.toLocaleString()}
                            {!Number.isFinite(Number(p.awardAmount)) || Number(p.awardAmount) <= 0
                              ? ' (chapter default)'
                              : ''}
                          </span>
                          <span style={{ flex: 1 }} />
                          <label htmlFor={`website-${p.id}`}><strong>Website:</strong></label>
                          <input
                            id={`website-${p.id}`}
                            type="text"
                            value={websiteById[p.id] ?? p.website ?? ""}
                            onChange={(e) => handleFieldChange(p.id, "website", e.target.value)}
                            placeholder="https://..."
                            style={{ flex: 2, minWidth: 180, padding: '3px 8px', fontFamily: 'inherit', border: '2px solid', borderColor: 'var(--bevel-sunken)', boxShadow: 'none', background: 'var(--gnf-bg)', boxSizing: 'border-box' }}
                          />
                        </div>

                        {/* About section */}
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 4 }}>
                            <label htmlFor={`about-${p.id}`}><strong>About section:</strong></label>
                            {/* Manually logged awards have no application to
                                generate from, so the AI button is hidden. */}
                            {!isManualAward(p) && (
                              <RetroButton
                                size="sm"
                                onClick={() => handleGenerateAbout(p.id)}
                                disabled={generatingAboutId === p.id}
                                style={{ textTransform: 'none' }}
                              >
                                {generatingAboutId === p.id ? "Generating…" : "Generate from application"}
                              </RetroButton>
                            )}
                          </div>
                          <textarea
                            id={`about-${p.id}`}
                            rows={3}
                            value={aboutById[p.id] ?? p.about ?? ""}
                            onChange={(e) => handleFieldChange(p.id, "about", e.target.value)}
                            style={{ display: 'block', width: '100%', padding: '6px 10px', fontFamily: 'inherit', fontSize: '0.95em', border: '2px solid', borderColor: 'var(--bevel-sunken)', boxShadow: 'none', background: 'var(--gnf-bg)', boxSizing: 'border-box', resize: 'vertical' }}
                            placeholder="Write a short public description about this founder or business..."
                          />
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <RetroButton
                            variant={pendingChanges[p.id] ? 'primary' : 'default'}
                            onClick={() => handleSaveAllChanges(p.id)}
                            style={{ textTransform: 'none' }}
                            disabled={!pendingChanges[p.id]}
                          >
                            Save all changes
                          </RetroButton>
                        </div>
                      </div>
                    </div>
                  </div>
                  );
                })}
                </div>
              </>
            );
}
